/**
 * Level-Up Gamification Store (Zustand + Firestore)
 *
 * Tracks user's level progress through educational milestones:
 *   Level 0: Default (Getting Started)
 *   Level 1: Complete 5 onboarding tasks (0% discount)
 *   Level 2: Pass Quiz 1 (1% discount)
 *   Level 3: Pass Quiz 2 (2% discount)
 *   Level 4: Pass Quiz 3 (4% discount)
 *   Level 5: Pass Portfolio Design Challenge (8% discount)
 *
 * Data lives in `users/{uid}` document fields: levelProgress, notifications
 */

import { create } from 'zustand';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logger } from '../services/loggingService';
import { getErrorMessage } from '../utils/validation';
import { QUIZ_PASSING_SCORE } from '../data/levelUpConfig';
import type {
  LevelProgress,
  QuizResult,
  QuizId,
  ChallengePortfolioSnapshot,
  ChallengeResult,
} from '../types/user';

// ── Helpers ──────────────────────────────────────────────────────────────────

const DEFAULT_PROGRESS: LevelProgress = {
  currentLevel: 0,
  quizResults: {},
  levelUpHistory: [],
};

/**
 * Derive the correct level from onboarding + quiz + challenge state.
 * This is the single source of truth — we never rely on a stored `currentLevel`
 * alone; we recompute it so it self-heals from any inconsistency.
 */
export function computeLevel(
  starsEarned: number,
  quizResults: LevelProgress['quizResults'] | undefined,
  challengeResult?: ChallengeResult,
): number {
  // Defensive: a partially-written `levelProgress` map has no `quizResults` at
  // all, and reading `.quiz3` off undefined threw inside the load, which the
  // catch swallowed — so progress silently stopped loading and the level never
  // advanced ([[BUGS #156]]).
  quizResults = quizResults ?? {};
  if (challengeResult?.passed && quizResults.quiz3?.passed && quizResults.quiz2?.passed && quizResults.quiz1?.passed && starsEarned >= 5) return 5;
  if (quizResults.quiz3?.passed && quizResults.quiz2?.passed && quizResults.quiz1?.passed && starsEarned >= 5) return 4;
  if (quizResults.quiz2?.passed && quizResults.quiz1?.passed && starsEarned >= 5) return 3;
  if (quizResults.quiz1?.passed && starsEarned >= 5) return 2;
  if (starsEarned >= 5) return 1;
  return 0;
}

/** Which quiz unlocks at which level. */
export function isQuizUnlocked(quizId: QuizId, currentLevel: number): boolean {
  switch (quizId) {
    case 'quiz1': return currentLevel >= 1;
    case 'quiz2': return currentLevel >= 2;
    case 'quiz3': return currentLevel >= 3;
    default: return false;
  }
}

/** Challenge unlocks at level 4. */
export function isChallengeUnlocked(currentLevel: number): boolean {
  return currentLevel >= 4;
}

/** Where each Get-Started task is actually done ([[BUGS #306]]).
 *
 *  The level-0 milestone routed every reader to `/` — the landing page, the
 *  one place the checklist cannot be advanced from — while every other
 *  milestone routed to `/academy`. A "Go" button that goes nowhere useful is
 *  worse than none: it reads as the product not knowing its own state.
 */
export const TASK_ROUTES: Record<string, string> = {
  firstSearch: '/research',
  firstChat: '/chat',
  firstPortfolio: '/portfolio/risk-assessment',
  firstRiskAnalysis: '/portfolio/risk-assessment',
  // The profile quiz is a modal the profile page owns, not a route of its own.
  quizCompleted: '/profile',
};

/** The order the checklist presents them — the funnel order (PRODUCT.md),
 *  matching `OnboardingChecklistContext`'s own `TASK_DEFINITIONS`. */
export const TASK_ORDER = [
  'firstSearch', 'firstChat', 'firstPortfolio', 'firstRiskAnalysis', 'quizCompleted',
] as const;

/** Human-readable next milestone.
 *
 *  `tasks` is optional so existing callers keep working; when it is given,
 *  the level-0 milestone points at the first task still outstanding rather
 *  than at a generic page.
 */
export function getNextMilestone(
  currentLevel: number,
  tasks?: Partial<Record<string, boolean>>,
): { type: string; label: string; route: string } | null {
  switch (currentLevel) {
    case 0: {
      const next = TASK_ORDER.find((id) => !tasks?.[id]);
      return {
        type: 'onboarding',
        label: 'Complete Get Started checklist',
        // No task state to read: send them to the first step, never to `/`.
        route: TASK_ROUTES[next ?? 'firstSearch'],
      };
    }
    case 1: return { type: 'quiz', label: 'Pass Investing Essentials quiz', route: '/academy' };
    case 2: return { type: 'quiz', label: 'Pass Reading the Numbers quiz', route: '/academy' };
    case 3: return { type: 'quiz', label: 'Pass Thinking Like an Analyst quiz', route: '/academy' };
    case 4: return { type: 'challenge', label: 'Complete Portfolio Design Challenge', route: '/academy' };
    default: return null; // Level 5 — max
  }
}

/** Progress percentage within current level (for ring). */
export function getLevelProgressPercent(
  currentLevel: number,
  starsEarned: number,
  quizResults: LevelProgress['quizResults'],
): number {
  switch (currentLevel) {
    case 0: return Math.round((starsEarned / 5) * 100);
    case 1: return quizResults.quiz1 ? Math.min(100, Math.round((quizResults.quiz1.score / QUIZ_PASSING_SCORE) * 100)) : 0;
    case 2: return quizResults.quiz2 ? Math.min(100, Math.round((quizResults.quiz2.score / QUIZ_PASSING_SCORE) * 100)) : 0;
    case 3: return quizResults.quiz3 ? Math.min(100, Math.round((quizResults.quiz3.score / QUIZ_PASSING_SCORE) * 100)) : 0;
    case 4: return 0; // challenge is pass/fail
    case 5: return 100;
    default: return 0;
  }
}

// ── Store ────────────────────────────────────────────────────────────────────

interface LevelUpState {
  // State
  levelProgress: LevelProgress;
  loaded: boolean;
  starsEarned: number; // from OnboardingTasks

  // Actions
  loadProgress: (userId: string) => Promise<void>;
  recordQuizResult: (userId: string, quizId: QuizId, score: number, totalQuestions: number) => Promise<void>;
  recordChallengeResult: (userId: string, passed: boolean, snapshot: ChallengePortfolioSnapshot) => Promise<void>;
  markRiskAnalysisViewed: (userId: string) => Promise<void>;
  syncStarsEarned: (stars: number) => void;
  reset: () => void;
}

export const useLevelUpStore = create<LevelUpState>((set, get) => ({
  levelProgress: { ...DEFAULT_PROGRESS },
  loaded: false,
  starsEarned: 0,

  loadProgress: async (userId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        set({ loaded: true });
        return;
      }

      const data = userDoc.data();
      // MERGE over the defaults, never replace them. `markRiskAnalysisViewed`
      // (and the self-heal below) write dotted paths, so Firestore can create a
      // `levelProgress` map holding one key and no `quizResults` — truthy, so a
      // `??` default never applies, and every reader downstream then works on a
      // shape that is missing the field it needs ([[BUGS #156]]).
      const stored: LevelProgress = { ...DEFAULT_PROGRESS, ...(data.levelProgress ?? {}) };
      const stars: number = data.onboardingTasks?.starsEarned ?? 0;
      // Carry riskAnalysisViewed forward — stored on the levelProgress sub-document.
      if (data.levelProgress?.riskAnalysisViewed) {
        stored.riskAnalysisViewed = true;
      }

      // Recompute level from source-of-truth data
      const correctLevel = computeLevel(stars, stored.quizResults, stored.challengeResult);

      set({
        levelProgress: {
          ...stored,
          currentLevel: correctLevel,
          levelUpHistory: stored.levelUpHistory ?? [],
        },
        starsEarned: stars,
        loaded: true,
      });

      // Self-heal if stored level is stale
      if (stored.currentLevel !== correctLevel) {
        await updateDoc(doc(db, 'users', userId), {
          'levelProgress.currentLevel': correctLevel,
        });
      }
    } catch (err) {
      logger.error('Failed to load level progress', {
        error: getErrorMessage(err),
      });
      set({ loaded: true });
    }
  },

  recordQuizResult: async (userId: string, quizId: QuizId, score: number, totalQuestions: number) => {
    const { levelProgress, starsEarned } = get();
    const scorePercent = Math.round((score / totalQuestions) * 100);
    const passed = scorePercent >= QUIZ_PASSING_SCORE;

    const existing = levelProgress.quizResults[quizId];
    const quizResult: QuizResult = {
      score: scorePercent,
      passed,
      completedAt: new Date(),
      attempts: (existing?.attempts ?? 0) + 1,
    };

    const updatedQuizResults = {
      ...levelProgress.quizResults,
      [quizId]: quizResult,
    };

    const newLevel = computeLevel(starsEarned, updatedQuizResults, levelProgress.challengeResult);
    const leveledUp = newLevel > levelProgress.currentLevel;

    const updatedHistory = leveledUp
      ? [...levelProgress.levelUpHistory, { level: newLevel, unlockedAt: new Date() }]
      : levelProgress.levelUpHistory;

    const updated: LevelProgress = {
      ...levelProgress,
      quizResults: updatedQuizResults,
      currentLevel: newLevel,
      levelUpHistory: updatedHistory,
      lastLevelUpAt: leveledUp ? new Date() : levelProgress.lastLevelUpAt,
    };

    set({ levelProgress: updated });

    try {
      await updateDoc(doc(db, 'users', userId), {
        'levelProgress': updated,
      });
      logger.logCrud('update', 'levelProgress', userId, true);
    } catch (err) {
      logger.error('Failed to save quiz result', {
        error: getErrorMessage(err),
        quizId,
      });
    }
  },

  recordChallengeResult: async (userId: string, passed: boolean, snapshot: ChallengePortfolioSnapshot) => {
    const { levelProgress, starsEarned } = get();

    const existing = levelProgress.challengeResult;
    const challengeResult: ChallengeResult = {
      passed,
      completedAt: new Date(),
      attempts: (existing?.attempts ?? 0) + 1,
      portfolioSnapshot: snapshot,
    };

    const newLevel = computeLevel(starsEarned, levelProgress.quizResults, challengeResult);
    const leveledUp = newLevel > levelProgress.currentLevel;

    const updatedHistory = leveledUp
      ? [...levelProgress.levelUpHistory, { level: newLevel, unlockedAt: new Date() }]
      : levelProgress.levelUpHistory;

    const updated: LevelProgress = {
      ...levelProgress,
      challengeResult,
      currentLevel: newLevel,
      levelUpHistory: updatedHistory,
      lastLevelUpAt: leveledUp ? new Date() : levelProgress.lastLevelUpAt,
    };

    set({ levelProgress: updated });

    try {
      await updateDoc(doc(db, 'users', userId), {
        'levelProgress': updated,
      });
      logger.logCrud('update', 'levelProgress', userId, true);
    } catch (err) {
      logger.error('Failed to save challenge result', {
        error: getErrorMessage(err),
      });
    }
  },

  markRiskAnalysisViewed: async (userId: string) => {
    const { levelProgress } = get();
    if (levelProgress.riskAnalysisViewed) return; // already recorded — no-op

    const updated: LevelProgress = { ...levelProgress, riskAnalysisViewed: true };
    set({ levelProgress: updated });

    try {
      await updateDoc(doc(db, 'users', userId), {
        'levelProgress.riskAnalysisViewed': true,
      });
    } catch (err) {
      logger.error('Failed to mark risk analysis viewed', {
        error: getErrorMessage(err),
      });
    }
  },

  syncStarsEarned: (stars: number) => {
    const { levelProgress } = get();
    const newLevel = computeLevel(stars, levelProgress.quizResults, levelProgress.challengeResult);

    set({
      starsEarned: stars,
      levelProgress: {
        ...levelProgress,
        currentLevel: newLevel,
        },
    });
  },

  reset: () => {
    set({
      levelProgress: { ...DEFAULT_PROGRESS },
      loaded: false,
      starsEarned: 0,
    });
  },
}));
